// The Understreet — Meridian's decked-over old street level, built to the
// night-city benchmark (night city ANALYSIS §e).
//
// ---------------------------------------------------------------------------
// WHY THIS FILE WAS REBUILT
//
// The previous district PASSED the audit's measurable laws — 9.40% emissive
// coverage at Y≥60 against the reference's 9.28%, YHIGH 58.1 against 58.0 —
// and looked nothing like the reference, which is the whole reason this
// asset program exists. Putting the two frames' coverage maps side by side
// says exactly why. Per-cell share of all Y≥60 pixels, 10×6 grid:
//
//   reference t008                      Bebop (the frame that "passed")
//   . . .79 . . . . . . .               .26 . . .08 . . . . . .
//   . .41 1.02 .12 . . . . . .          .27 .36 . .16 .08 . . .18 . .
//   . .39 .91 . . . . . .07 .62         .40 .13 .10 . .31 . .27 .21 . .44
//   . .10 .18 . . . .24 . .61 1.11      .39 .34 .41 .38 .99 .23 .42 .34 . .15
//   . . .09 .41 . . . .09 .21 .58       .52 .37 .27 .13 .06 .16 .47 .45 .05 .
//   . . .09 . . .06 .09 . .12 .44       . . . . . . . . . .
//
// The reference concentrates: the hero banner's three cells carry 2.7% of
// the WHOLE FRAME and run a cell mean of Y102, the right-hand ad wall
// carries another 3.1% at cell mean Y110, and everything else is empty at
// Y30–45 with corners at Y15. Ours smeared: no cell above 0.99%, and a
// uniform Y40–55 wash across two-thirds of the plate. Same total, opposite
// picture. The instrument that produced that wash was a lit-window GRID
// tiled down 500 m of wall — the one thing §e names as never present in the
// reference — and once you own the wall area with windows there is no dark
// left for the signs to be loud against.
//
// So the rebuild is four moves, in this order of importance:
//
//   1. THE WALLS GO DARK. No window grids. Facades are near-black massing
//      with 1–2 px specks under Y60, exactly as §e specifies, and the only
//      window feature is a handful of interior-lit BOXES with real recesses
//      and occluders.
//   2. THE SIGNS CARRY IMAGES. Every fixture is a procedurally forged
//      poster from render/signage.ts — landscapes with aerial perspective,
//      portraits with a key and a rim, vessels, brand panels, figure
//      strips — because §d's finding is that the reference's ad content is
//      photographic compositing, and a two-stop gradient with scratches on
//      it is not that.
//   3. THE SIGNS THROW LIGHT. Each fixture gets a blurred copy of its own
//      face as an additive decal on the wall behind it, an anchored haze
//      patch, and a rippled smear of itself on the wet ground. §d: "haze
//      luminance is the glow medium — brighter around/above signage".
//   4. THE HAZE SWALLOWS. Exponential fog plus stacked haze curtains, so
//      the far massing dissolves rather than merely darkening — in the
//      reference the towers are barely visible at all.
//
// Held from the old build, because they were right: the two-accent census
// with white, the one hologram, the two subliminal traffic lanes, the
// no-bloom/no-CA/no-grain post stack, the scene's own linear tonemap.

import {
  type AreaPlan,
  type NightBill,
  rand2,
  UNDERSTREET_CLUTTER,
  UNDERSTREET_HALF_D,
  UNDERSTREET_HALF_W,
  UNDERSTREET_PILLARS,
  UNDERSTREET_PLINTH,
} from "@bebop/shared";
import * as THREE from "three";
import { installProbe } from "./atmosphere";
import {
  type AuthoredPlacement,
  bindAuthoredEnvironment,
  disposeAuthoredObject,
  instanceAuthoredAsset,
} from "./authored-assets";
import { type AreaScene, boxFromAabb, Tracker } from "./common";
import { hash1 } from "./forge";
import { NIGHT_DISTRICT_POST } from "./post";
import {
  deckSoffit,
  drawBrandPanel,
  drawFacade,
  drawFigureStrip,
  drawHeroBanner,
  drawInteriorBand,
  drawNeonPanel,
  drawPortraitPoster,
  drawTravelPoster,
  drawVesselPoster,
  hazeCurtain,
  hazePatch,
  makePanel,
  type Panel,
  wetStreet,
} from "./signage";
import { catenaryGeometry } from "./solid";

/** Base haze — the "black" sky that is not black (measured #233037). */
const HAZE = 0x243440;
/** The deck: the ceiling of the whole district. */
const DECK_Y = 30;
const SEED = 0x0575ee7;
const W = UNDERSTREET_HALF_W;
const D = UNDERSTREET_HALF_D;

// ---------------------------------------------------------------------------
// Fixture mounting
//
// One function does every sign in the district, because every sign in the
// district needs the same four things and the old build gave them to some
// fixtures and not others — which is why half of them lit nothing.

interface MountOptions {
  /** Metres. */
  x: number;
  y: number;
  /** Which wall: −1 = north (z = −D), +1 = south. `0` = free-hanging. */
  side: -1 | 0 | 1;
  /** Free-hanging fixtures need their own z and facing. */
  z?: number;
  yaw?: number;
  w: number;
  h: number;
  /** Wall-glow strength; the spill decal spans `spread` × the fixture. */
  spread?: number;
  spillAlpha?: number;
  /** Anchored haze patch radius in metres (0 to skip). */
  haze?: number;
  hazeAlpha?: number;
  /** Ground reflection length in metres (0 to skip). */
  reflect?: number;
  reflectAlpha?: number;
  /** Draw a box frame and a cable drop. */
  frame?: boolean;
  cables?: boolean;
  /** A second, dimmer face on the back — the double-sided banner trick. */
  doubleFaced?: boolean;
}

/**
 * Hang a forged panel and give it its light.
 *
 * The four parts, in the order the audit ranks them:
 *   FACE     — an unlit quad carrying the poster.
 *   SPILL    — a blurred copy of the same poster, additive on the wall
 *              around it. This is the "glow bleeding onto nearby geometry"
 *              the district had none of; it costs one quad and it is what
 *              tells the eye the sign is a light and not a sticker.
 *   HAZE     — an anchored patch of lit fog at the fixture. §d calls haze
 *              luminance the glow medium and says the halos hug signage.
 *   REFLECT  — the poster smeared down the wet paving toward the camera.
 */
/** Original Blender solids with the district's established dark material
 * grade. Lightbox artwork and their atmospheric spill remain live surfaces. */
function underAsset(
  t: Tracker,
  parent: THREE.Object3D,
  id: string,
  placements: AuthoredPlacement[],
  unlit = false,
): THREE.Group {
  const group = instanceAuthoredAsset(id, placements);
  const replacements = new Map<THREE.Material, THREE.Material>();
  const graded = new Set<THREE.Material>();
  group.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return;
    object.castShadow = false;
    const grade = (material: THREE.Material): THREE.Material => {
      if (!(material instanceof THREE.MeshStandardMaterial)) return material;
      if (!unlit) {
        if (!graded.has(material)) {
          material.color.multiplyScalar(0.26);
          graded.add(material);
        }
        return material;
      }
      let copy = replacements.get(material);
      if (!copy) {
        copy = t.mat(
          new THREE.MeshBasicMaterial({
            map: material.map,
            color: material.color.clone().multiplyScalar(0.075),
          }),
        );
        replacements.set(material, copy);
        material.dispose();
      }
      return copy;
    };
    object.material = Array.isArray(object.material)
      ? object.material.map(grade)
      : grade(object.material);
  });
  parent.add(group);
  t.cleanup(() => disposeAuthoredObject(group));
  return group;
}

function mount(t: Tracker, scene: THREE.Scene, panel: Panel, o: MountOptions): THREE.Group {
  const g = new THREE.Group();
  const yaw = o.side === 0 ? (o.yaw ?? 0) : o.side < 0 ? 0 : Math.PI;
  const z = o.side === 0 ? (o.z ?? 0) : o.side * (D - 0.5);
  g.position.set(o.x, o.y, z);
  g.rotation.y = yaw;

  // The light the sign throws onto the wall behind it. Drawn FIRST and
  // furthest back so the face and frame paint over its middle.
  //
  // Wall-mounted only. A free-hanging banner has no wall to spill onto, and
  // the first pass gave it one anyway: the decal hung in mid-air behind the
  // hero and rendered as a glowing RECTANGLE with visible edges, which is
  // the most conspicuous artefact a night frame can carry. Free-hanging
  // fixtures get their light from the anchored haze sprite instead, which
  // is what a lit banner in fog actually looks like from any angle.
  const spread = o.spread ?? 2.4;
  if (o.side !== 0) {
    const spill = new THREE.Mesh(
      t.geo(new THREE.PlaneGeometry(o.w * spread, o.h * spread * 0.9)),
      t.mat(
        new THREE.MeshBasicMaterial({
          map: panel.spill,
          transparent: true,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          opacity: o.spillAlpha ?? 0.5,
        }),
      ),
    );
    spill.position.z = 0.06;
    spill.renderOrder = -1;
    g.add(spill);
  }

  if (o.frame ?? true) {
    underAsset(
      t,
      g,
      "city-sign-frame",
      [{ x: 0, y: (-o.h * 1.05) / 2, z: 0.14, width: o.w * 1.06, height: o.h * 1.05, depth: 0.26 }],
      true,
    );
  }

  const face = new THREE.Mesh(
    t.geo(new THREE.PlaneGeometry(o.w, o.h)),
    t.mat(new THREE.MeshBasicMaterial({ map: panel.face })),
  );
  face.position.z = 0.29;
  g.add(face);
  if (o.doubleFaced) {
    // One texture on two quads, back face dimmer — the reference's exact
    // construction, and the cheapest way to make a hanging sign read as an
    // object with a thickness rather than as a decal.
    const back = new THREE.Mesh(
      t.geo(new THREE.PlaneGeometry(o.w, o.h)),
      t.mat(new THREE.MeshBasicMaterial({ map: panel.face, color: 0x7d858a })),
    );
    back.rotation.y = Math.PI;
    back.position.z = -0.01;
    g.add(back);
  }

  if (o.cables) {
    const cableMat = t.mat(new THREE.MeshBasicMaterial({ color: 0x141a1f }));
    for (let i = 0; i < 4; i++) {
      const dx = (i - 1.5) * o.w * 0.28;
      const drop = DECK_Y - o.y - o.h / 2;
      const cable = new THREE.Mesh(
        t.geo(new THREE.CylinderGeometry(0.045, 0.045, drop, 4)),
        cableMat,
      );
      cable.position.set(dx, o.h / 2 + drop / 2, 0.1);
      cable.rotation.z = dx * 0.004;
      g.add(cable);
    }
    // The greeble the audit says sells scale on a hero fixture: a fan unit
    // bolted to the same drop.
    const fan = new THREE.Mesh(
      t.geo(new THREE.BoxGeometry(1.5, 1.5, 1.1)),
      t.mat(new THREE.MeshBasicMaterial({ color: 0x171e23 })),
    );
    fan.position.set(o.w * 0.66, o.h * 0.42, 0);
    g.add(fan);
  }
  scene.add(g);

  // Anchored haze: a patch of lit fog sitting at the fixture, in the
  // fixture's own colour. A sprite, so it holds under any camera.
  //
  // Offset ABOVE the fixture, not centred on it. Centred, this sprite adds
  // itself to the face underneath and pushed the brightest signs to a hard
  // 255 — over the "no clipped whites, brightest cores ≤225" law, which is
  // one of the few the night bar states as an absolute.
  const hz = o.haze ?? o.w * 1.5;
  if (hz > 0) {
    const sprite = new THREE.Sprite(
      t.mat(
        new THREE.SpriteMaterial({
          map: hazePatch(t, panel.tint.replace("rgb(", "rgba(").replace(")", ",0.85)"), o.x | 0),
          transparent: true,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          opacity: o.hazeAlpha ?? Math.min(0.11, 0.03 + (panel.power / 255) * 0.11),
        }),
      ),
    );
    sprite.position.set(o.x, o.y + o.h * 0.42, z + (o.side === 0 ? 0 : -o.side * 1.6));
    sprite.scale.set(hz, hz * 0.85, 1);
    scene.add(sprite);
  }

  // The wet street underneath. Runs from the fixture's foot toward the
  // canyon centre, which is where a viewer standing in the street sees it.
  const len = o.reflect ?? o.h * 2.2;
  if (len > 0 && o.y < 22) {
    const refl = new THREE.Mesh(
      t.geo(new THREE.PlaneGeometry(o.w * 1.5, len)),
      t.mat(
        new THREE.MeshBasicMaterial({
          map: panel.reflect,
          transparent: true,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          opacity: o.reflectAlpha ?? 0.55,
        }),
      ),
    );
    // Euler XYZ composes Z first in the local frame, so the π roll flips the
    // sheet end-for-end BEFORE the tilt — which puts the reflection's head
    // (the foot of the sign) against whichever wall the fixture is on.
    refl.rotation.set(-Math.PI / 2, 0, o.side > 0 ? Math.PI : 0);
    const inward = o.side === 0 ? 0 : -o.side * (len / 2 - 0.5);
    refl.position.set(o.x, 0.03, z + inward);
    refl.renderOrder = 1;
    scene.add(refl);
  }
  return g;
}

// ---------------------------------------------------------------------------
// Sky traffic — unchanged in intent, two lanes of subliminal specks

class SkyTraffic {
  readonly points: THREE.Points;
  private positions: Float32Array;
  private speeds: number[] = [];

  constructor(t: Tracker) {
    const COUNT = 64;
    this.positions = new Float32Array(COUNT * 3);
    for (let i = 0; i < COUNT; i++) {
      const lane = i % 2;
      this.positions[i * 3] = -W - 20 - rand2(SEED + 60, i, 0) * 210;
      this.positions[i * 3 + 1] = 21 + lane * 4.5 + rand2(SEED + 61, i, 0) * 2.4;
      this.positions[i * 3 + 2] = (lane === 0 ? -13 : 11) + rand2(SEED + 62, i, 0) * 5;
      this.speeds.push((lane === 0 ? 1 : -1) * (2.6 + rand2(SEED + 63, i, 0) * 2));
    }
    const geo = t.geo(new THREE.BufferGeometry());
    geo.setAttribute("position", new THREE.BufferAttribute(this.positions, 3));
    this.points = new THREE.Points(
      geo,
      t.mat(
        new THREE.PointsMaterial({
          // Only +10–25 luma over the haze band: subliminal at normal
          // exposure, visibly displaced under a 1 s difference.
          color: 0x4c5f6d,
          size: 2.6,
          sizeAttenuation: false,
          transparent: true,
          opacity: 0.9,
          depthWrite: false,
        }),
      ),
    );
    this.points.frustumCulled = false;
  }

  tick(dt: number): void {
    const lo = -W - 235;
    const hi = W + 15;
    for (let i = 0; i < this.speeds.length; i++) {
      let x = this.positions[i * 3] ?? 0;
      x += (this.speeds[i] ?? 0) * dt;
      if (x > hi) x = lo;
      if (x < lo) x = hi;
      this.positions[i * 3] = x;
    }
    (this.points.geometry.getAttribute("position") as THREE.BufferAttribute).needsUpdate = true;
  }
}

/** The one animated emissive: a figure on a plinth, scanlines rolling over
 *  a fixed silhouette — static model, animated shader (§d, resolved). */
function buildHologram(t: Tracker, parent: THREE.Object3D): THREE.ShaderMaterial {
  const material = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    uniforms: { time: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform float time;
      varying vec2 vUv;
      void main() {
        vec2 p = vUv - vec2(0.5, 0.0);
        float body = smoothstep(0.16, 0.13, abs(p.x)) * smoothstep(0.05, 0.1, vUv.y) * smoothstep(0.62, 0.55, vUv.y);
        float head = 1.0 - smoothstep(0.07, 0.1, length(vUv - vec2(0.5, 0.66)));
        float horn = smoothstep(0.035, 0.02, abs((vUv.y - 0.62) - (vUv.x - 0.56) * 0.8)) *
                     step(0.56, vUv.x) * step(vUv.x, 0.78) * step(0.55, vUv.y);
        float fig = clamp(body + head + horn, 0.0, 1.0);
        float scan = 0.72 + 0.28 * sin((vUv.y - time * 0.11) * 240.0);
        float flicker = 0.85 + 0.15 * sin(time * 9.0 + sin(time * 3.7) * 2.0);
        float edge = smoothstep(0.0, 0.06, vUv.y) * (1.0 - smoothstep(0.9, 1.0, vUv.y));
        vec3 cyan = vec3(0.14, 0.45, 0.55);
        gl_FragColor = vec4(cyan * fig * scan * flicker * edge * 2.6, fig * 0.78 * edge);
      }
    `,
  });
  const quad = new THREE.Mesh(t.geo(new THREE.PlaneGeometry(2.6, 5.2)), material);
  const px = (UNDERSTREET_PLINTH.minX + UNDERSTREET_PLINTH.maxX) / 2;
  const pz = (UNDERSTREET_PLINTH.minZ + UNDERSTREET_PLINTH.maxZ) / 2;
  quad.position.set(px, 4.1, pz);
  quad.rotation.y = -0.4;
  parent.add(quad);
  parent.add(
    boxFromAabb(
      t,
      UNDERSTREET_PLINTH,
      1.4,
      t.mat(new THREE.MeshBasicMaterial({ color: 0x13181c })),
    ),
  );
  const lens = new THREE.Mesh(
    t.geo(new THREE.CylinderGeometry(0.3, 0.4, 0.3, 8)),
    t.mat(new THREE.MeshBasicMaterial({ color: 0x2c8ea0 })),
  );
  lens.position.set(px, 1.55, pz);
  parent.add(lens);
  return material;
}

// ---------------------------------------------------------------------------

export function createUnderstreetScene(_plan: AreaPlan): AreaScene {
  const t = new Tracker();
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(HAZE);
  // Exponential haze. Held at the value six rounds of luma work settled on;
  // the extra dissolve this rebuild needs comes from the curtains below,
  // which can eat the far end without also eating the near fixtures the way
  // a denser exponential term would.
  scene.fog = new THREE.FogExp2(HAZE, 0.0125);

  const worldAnchor = new THREE.Group();
  scene.add(worldAnchor);

  // --- The wet street -------------------------------------------------------
  // Roughness is the whole trick: the map collapses toward 0.05 where water
  // stands and holds 0.92 on dry stone, so the same material is matte
  // between the puddles and a mirror inside them.
  const wet = wetStreet(t, 12);
  const floorW = W * 2 + 240;
  const floorD = D * 2 + 60;
  for (const map of [wet.albedo, wet.normal, wet.rough]) {
    map.repeat.set(floorW / 5, floorD / 5);
  }
  const floor = new THREE.Mesh(
    t.geo(new THREE.PlaneGeometry(floorW, floorD)),
    t.mat(
      new THREE.MeshStandardMaterial({
        map: wet.albedo,
        normalMap: wet.normal,
        normalScale: new THREE.Vector2(0.9, 0.9),
        roughnessMap: wet.rough,
        roughness: 1,
        metalness: 0.12,
        // The paving is the district's biggest single luma lever. Both night
        // framings give it ~30% of the plate, the reference gives its canyon
        // no floor at all, and the first pass of this rebuild let it fall to
        // a mean Y7 across the bottom sixth of frame — against a reference
        // whose darkest band is Y15 and whose fog floor never goes under 26.
        // A night street is dark; it is not a hole.
        color: 0xa9b5be,
      }),
    ),
  );
  floor.rotation.x = -Math.PI / 2;
  floor.position.x = -60;
  scene.add(floor);

  // --- Canyon massing -------------------------------------------------------
  // Near-black boxes with speck windows, in four silhouette ranks. Unlit
  // MeshBasic: §f is explicit that the towers are unlit massing and that
  // darkness plus fog does the depth work, and lighting them is how a night
  // canyon turns into a grey model of a night canyon.
  const facadeTex = [0, 1, 2].map((i) => {
    const canvas = document.createElement("canvas");
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (ctx) drawFacade(ctx, 256, 256, 91 + i * 17);
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.anisotropy = 4;
    return t.tex(tex);
  });
  const canyon: AuthoredPlacement[] = [];
  const outer: AuthoredPlacement[] = [];
  for (const side of [-1, 1]) {
    let x = -W - 120;
    while (x < W + 6) {
      const width = 22 + rand2(SEED + 8, Math.round(x), side) * 30;
      const height = 26 + rand2(SEED + 9, Math.round(x), side) * 14;
      const depth = 10 + rand2(SEED + 10, Math.round(x), side) * 14;
      const inset = rand2(SEED + 11, Math.round(x), side) * 10;
      canyon.push({
        x: x + width / 2,
        y: 0,
        z: side * (D + depth / 2 + inset),
        width,
        height,
        depth,
      });
      x += width + 2 + rand2(SEED + 12, Math.round(x), side) * 6;
    }
    x = -W - 140;
    while (x < W + 40) {
      const width = 30 + rand2(SEED + 13, Math.round(x), side) * 40;
      const height = 30 + rand2(SEED + 14, Math.round(x), side) * 12;
      outer.push({
        x: x + width / 2,
        y: 0,
        z: side * (D + 26 + rand2(SEED + 15, Math.round(x), side) * 12),
        width,
        height,
        depth: 16,
      });
      x += width + 6;
    }
  }
  underAsset(t, scene, "city-slab", canyon, true);
  underAsset(t, scene, "city-stepped", outer, true);
  const farTex = (facadeTex[2] as THREE.CanvasTexture).clone();
  farTex.wrapS = THREE.RepeatWrapping;
  farTex.wrapT = THREE.RepeatWrapping;
  farTex.repeat.set(240 / 13, 90 / 13);
  farTex.needsUpdate = true;
  t.tex(farTex);
  const farWall = new THREE.Mesh(
    t.geo(new THREE.PlaneGeometry(240, 90)),
    t.mat(new THREE.MeshBasicMaterial({ map: farTex, color: 0x8f989e })),
  );
  farWall.position.set(-W - 200, 20, -40);
  farWall.rotation.y = Math.PI / 2;
  scene.add(farWall);

  // --- The deck -------------------------------------------------------------
  const deck = new THREE.Mesh(
    t.geo(new THREE.PlaneGeometry(W * 2 + 260, D * 2 + 80)),
    // Textured, not a flat plate: the reference's sky band carries a
    // measurable lateral gradient (60→48→41) and a plain colour fails the
    // "haze glow is light-source-anchored, not uniform" bar by construction.
    t.mat(
      new THREE.MeshBasicMaterial({
        map: (() => {
          const map = deckSoffit(t, 5);
          map.repeat.set((W * 2 + 260) / 24, (D * 2 + 80) / 24);
          return map;
        })(),
        // Solved, not chosen: the soffit sheet means ~0.72 of full, the
        // scene's fog lifts a surface 30 m off by 13% toward the Y49 haze
        // anchor, and the reference's sky band runs Y41–60. 0x2b3944 lands
        // the near deck at Y33 and the far deck at Y47.
        color: 0x2b3944,
        side: THREE.DoubleSide,
      }),
    ),
  );
  deck.rotation.x = Math.PI / 2;
  deck.position.set(-60, DECK_Y, 0);
  scene.add(deck);
  /**
   * Slit apertures: the only "sky" the Understreet has, and its key light.
   *
   * They are also the one thing in this set with nothing holding them up.
   * A 1.6 m unlit quad hung 20 cm under a 30 m ceiling reads, from any lens
   * that is not directly beneath it, as a pale untextured slab floating in
   * the dark — measured on the canyon plate at #4C5E7A against a deck at
   * #1C2832, with no relief, no edge and no support. The light is right;
   * the aperture was missing.
   *
   * So each slit gains its REVEAL: a shallow downstand collar in the deck's
   * own material, four thin walls round the opening, hanging 1.1 m below the
   * soffit. The quad now sits at the top of a light well that meets the
   * ceiling, which is what a roof slot looks like from underneath — and at
   * a grazing angle the near wall occludes the bright face instead of
   * leaving it hanging. All twenty walls ride one InstancedMesh, so the
   * anchor costs one draw call for the whole canyon.
   */
  const REVEAL_DROP = 1.1;
  const REVEAL_T = 0.35;
  const slits = [
    [-160, 30, 4],
    [-96, 34, -5],
    [-8, 40, 0],
    [52, 30, 6],
    [104, 34, -4],
  ] as const;
  const revealGeo = t.geo(new THREE.BoxGeometry(1, 1, 1));
  const reveals = new THREE.InstancedMesh(
    revealGeo,
    t.mat(new THREE.MeshBasicMaterial({ color: 0x222c36 })),
    slits.length * 4,
  );
  reveals.frustumCulled = false;
  const revealAt = new THREE.Object3D();
  let revealSlot = 0;
  scene.add(reveals);
  for (const [ax, aw, az] of slits) {
    const slit = new THREE.Mesh(
      t.geo(new THREE.PlaneGeometry(aw, 1.6)),
      t.mat(new THREE.MeshBasicMaterial({ color: 0x5c6f92, side: THREE.DoubleSide })),
    );
    slit.rotation.x = Math.PI / 2;
    slit.position.set(ax, DECK_Y - 0.2, az);
    scene.add(slit);
    // The collar: two long walls down the slit's sides, two short ends.
    const wall = (sx: number, sz: number, ox: number, oz: number): void => {
      revealAt.position.set(ax + ox, DECK_Y - 0.2 - REVEAL_DROP / 2, az + oz);
      revealAt.scale.set(sx, REVEAL_DROP, sz);
      revealAt.rotation.set(0, 0, 0);
      revealAt.updateMatrix();
      reveals.setMatrixAt(revealSlot++, revealAt.matrix);
    };
    wall(aw + REVEAL_T * 2, REVEAL_T, 0, 0.8 + REVEAL_T / 2);
    wall(aw + REVEAL_T * 2, REVEAL_T, 0, -0.8 - REVEAL_T / 2);
    wall(REVEAL_T, 1.6, aw / 2 + REVEAL_T / 2, 0);
    wall(REVEAL_T, 1.6, -aw / 2 - REVEAL_T / 2, 0);
    const veil = new THREE.Sprite(
      t.mat(
        new THREE.SpriteMaterial({
          map: hazePatch(t, "rgba(112,132,176,0.5)", ax | 0),
          transparent: true,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          opacity: 0.16,
        }),
      ),
    );
    veil.position.set(ax, DECK_Y - 8, az);
    veil.scale.set(aw + 30, 30, 1);
    scene.add(veil);
  }

  // --- The haze curtains ----------------------------------------------------
  // The move that makes the far canyon DISSOLVE rather than darken. Eight
  // sheets hung across the canyon at increasing depth, each torn by fbm and
  // dense at its foot, so anything behind them loses contrast a layer at a
  // time. This is what an exponential fog term alone cannot buy: raising
  // the density enough to erase a tower 120 m off also erases the fixture
  // run at 40 m, and the fixture run is the entire subject.
  // Hung from 24 m WEST of the near fixture run, not across it. The first
  // pass started them at x=60, i.e. two metres behind the hero banner and
  // squarely in front of the whole near canyon for the billboard lens,
  // which laid a flat grey veil over the foreground and cost the near field
  // its contrast. Fog belongs between the camera and the DISTANCE.
  const curtainTex = [0, 1, 2].map((i) => hazeCurtain(t, "rgb(42,58,70)", 4 + i * 9));
  for (let i = 0; i < 8; i++) {
    const cx = 24 - i * 32;
    const tex = curtainTex[i % 3] ?? curtainTex[0];
    const curtain = new THREE.Mesh(
      t.geo(new THREE.PlaneGeometry(D * 2 + 90, DECK_Y + 18)),
      t.mat(
        new THREE.MeshBasicMaterial({
          map: tex,
          transparent: true,
          depthWrite: false,
          opacity: 0.1 + i * 0.035,
          color: 0x9fb4c2,
          side: THREE.DoubleSide,
          fog: false,
        }),
      ),
    );
    curtain.rotation.y = Math.PI / 2;
    curtain.position.set(cx, (DECK_Y + 18) / 2 - 6, 0);
    curtain.renderOrder = 2;
    scene.add(curtain);
  }

  // --- The emissive economy -------------------------------------------------
  //
  // §e wants 10–20 designed fixtures across ≥5 archetypes per street-canyon
  // view, portrait-dominant, all static stills. It does NOT want them
  // evenly spread: the reference puts them in two loud clusters against a
  // dark canyon, which is the finding the coverage map above turns into a
  // rule. So the district is built as AD WALLS with dark between them.

  const P = {
    hero: makePanel(t, 224, 352, (c, w, h) => drawHeroBanner(c, w, h, 61), {
      seed: 6,
      tone: 0.06,
      grain: 0.04,
      vignette: 0.05,
      spillBoost: 1.5,
      reflectGain: 1.5,
    }),
    hero2: makePanel(t, 200, 320, (c, w, h) => drawHeroBanner(c, w, h, 77), {
      seed: 16,
      tone: 0.08,
      grain: 0.04,
      vignette: 0.05,
      spillBoost: 1.4,
      reflectGain: 1.4,
    }),
    travel: makePanel(t, 224, 288, (c, w, h) => drawTravelPoster(c, w, h, 11), {
      seed: 1,
      tone: 0.14,
      grain: 0.05,
      gain: 1.28,
    }),
    travel2: makePanel(t, 224, 288, (c, w, h) => drawTravelPoster(c, w, h, 131), {
      seed: 21,
      tone: 0.18,
      grain: 0.05,
      gain: 1.24,
    }),
    vessel: makePanel(t, 320, 192, (c, w, h) => drawVesselPoster(c, w, h, 21), {
      seed: 2,
      tone: 0.16,
      grain: 0.05,
      gain: 1.22,
    }),
    vessel2: makePanel(t, 288, 176, (c, w, h) => drawVesselPoster(c, w, h, 121), {
      seed: 22,
      tone: 0.2,
      grain: 0.05,
      gain: 1.2,
    }),
    portrait: makePanel(t, 224, 320, (c, w, h) => drawPortraitPoster(c, w, h, 31), {
      seed: 3,
      tone: 0.12,
      grain: 0.06,
      gain: 1.35,
    }),
    portrait2: makePanel(t, 208, 304, (c, w, h) => drawPortraitPoster(c, w, h, 231), {
      seed: 13,
      tone: 0.14,
      grain: 0.06,
      gain: 1.3,
    }),
    // The brand panels are where the census's low-sat WHITE lives (14.6% of
    // the reference's emissive pixels) and where its Y≥200 population lives
    // (0.58% of frame). They are near-white by construction and they are
    // deliberately the brightest thing in the district.
    brand: makePanel(t, 224, 272, (c, w, h) => drawBrandPanel(c, w, h, 41, "a"), {
      seed: 4,
      tone: 0.04,
      grain: 0.035,
      vignette: 0.07,
      spillBoost: 1.5,
      reflectGain: 1.5,
    }),
    brand2: makePanel(t, 208, 256, (c, w, h) => drawBrandPanel(c, w, h, 141, "b"), {
      seed: 14,
      tone: 0.05,
      grain: 0.035,
      vignette: 0.07,
      spillBoost: 1.4,
      reflectGain: 1.4,
    }),
    figures: makePanel(t, 176, 352, (c, w, h) => drawFigureStrip(c, w, h, 51), {
      seed: 5,
      tone: 0.14,
      grain: 0.06,
      gain: 1.25,
    }),
    figures2: makePanel(t, 160, 320, (c, w, h) => drawFigureStrip(c, w, h, 151), {
      seed: 15,
      tone: 0.16,
      grain: 0.06,
      gain: 1.2,
    }),
    neon: makePanel(t, 288, 144, (c, w, h) => drawNeonPanel(c, w, h, 71, "a"), {
      seed: 7,
      tone: 0.05,
      grain: 0.04,
      spillBoost: 1.7,
      reflectGain: 1.6,
    }),
    neon2: makePanel(t, 128, 384, (c, w, h) => drawNeonPanel(c, w, h, 171, "b"), {
      seed: 17,
      tone: 0.05,
      grain: 0.04,
      spillBoost: 1.7,
      reflectGain: 1.6,
    }),
    neon3: makePanel(t, 128, 384, (c, w, h) => drawNeonPanel(c, w, h, 271, "a"), {
      seed: 27,
      tone: 0.05,
      grain: 0.04,
      spillBoost: 1.7,
      reflectGain: 1.6,
    }),
    interior: makePanel(t, 512, 128, (c, w, h) => drawInteriorBand(c, w, h, 81), {
      seed: 8,
      tone: 0.1,
      grain: 0.05,
      vignette: 0,
      gain: 1.3,
      spillBoost: 0.75,
      reflectGain: 0.9,
    }),
    interior2: makePanel(t, 512, 128, (c, w, h) => drawInteriorBand(c, w, h, 181), {
      seed: 18,
      tone: 0.12,
      grain: 0.05,
      vignette: 0,
      gain: 1.3,
      spillBoost: 0.75,
      reflectGain: 0.9,
    }),
    // The kiosks get their own faces. The first pass hung a wall sign on a
    // serving hatch and the same brand panel then appeared three times in
    // one frame at three scales, which is exactly how a set reads as a kit
    // rather than as a street.
    hatchA: makePanel(t, 256, 128, (c, w, h) => drawNeonPanel(c, w, h, 311, "a"), {
      seed: 31,
      tone: 0.06,
      grain: 0.04,
      spillBoost: 1.5,
      noReflect: true,
    }),
    hatchB: makePanel(t, 256, 128, (c, w, h) => drawInteriorBand(c, w, h, 321), {
      seed: 32,
      tone: 0.08,
      grain: 0.05,
      vignette: 0,
      spillBoost: 1.3,
      noReflect: true,
    }),
  };

  // THE HERO. Both night framings look WEST down the canyon, so +z is the
  // camera's left in both; the hero hangs left and near for the billboard
  // vista, and its twin hangs left and near for the street vista 90 m west.
  mount(t, scene, P.hero, {
    x: 62,
    y: 14.5,
    side: 0,
    z: 8,
    yaw: Math.PI / 2 + 0.2,
    w: 8.6,
    h: 13.6,
    doubleFaced: true,
    cables: true,
    spread: 2.0,
    spillAlpha: 0.4,
    haze: 15,
    hazeAlpha: 0.12,
    reflect: 30,
    reflectAlpha: 0.8,
  });
  mount(t, scene, P.hero2, {
    x: -30,
    y: 13.4,
    side: 0,
    z: 6.5,
    yaw: Math.PI / 2 - 0.15,
    w: 7.2,
    h: 11.4,
    doubleFaced: true,
    cables: true,
    spread: 2.0,
    spillAlpha: 0.36,
    haze: 13,
    hazeAlpha: 0.1,
    reflect: 26,
    reflectAlpha: 0.72,
  });

  /**
   * An AD WALL: a stack of panels on one façade, mixed archetypes, unequal
   * sizes, unequal gaps. This is the reference's right-hand cluster — "ad
   * wall stack (Coca-Cola Aurebesh parody, alien/saucer-ship billboard,
   * trooper poster strips, cyan strip)" — and it is the shape of fixture
   * distribution that made the reference's coverage map concentrate.
   */
  const adWall = (
    x: number,
    side: -1 | 1,
    stack: ReadonlyArray<readonly [Panel, number, number, number]>,
  ): void => {
    for (const [panel, dx, y, w] of stack) {
      mount(t, scene, panel, {
        x: x + dx,
        y,
        side,
        w,
        h: w / panel.aspect,
        spread: 1.9,
        spillAlpha: 0.62,
        hazeAlpha: Math.min(0.11, 0.03 + (panel.power / 255) * 0.11),
        reflect: y < 12 ? 18 : 0,
        reflectAlpha: 0.6,
      });
    }
  };

  // Cluster 1 — the near-north wall, the billboard vista's answer to the
  // reference's right-hand stack. Sized up 40% over the first pass: a
  // 0.0125 exponential fog costs a fixture 41% of its value at 58 m, which
  // is where this wall sits from the billboard lens, so the Y≥120 budget
  // (5.4% of frame in the reference; 2.3% in the first pass) can only be
  // bought in fixture AREA, not in fixture brightness — brightness is
  // already at the 225 ceiling.
  adWall(46, -1, [
    [P.brand, -11, 17.5, 8.4],
    [P.vessel, 3, 18.4, 13],
    [P.figures, 14.5, 11.5, 5.6],
    [P.travel, -11, 9.2, 7.4],
    [P.neon, 1.5, 8.6, 10],
  ]);
  // Cluster 2 — the far-south wall, deeper into the haze, the second
  // cluster both framings read as a lit block in the fog.
  adWall(-48, 1, [
    [P.portrait, -9, 16.4, 7.2],
    [P.brand2, 3, 17.6, 7.6],
    [P.travel2, 13, 11, 6.6],
    [P.figures2, -11, 9.2, 5],
    [P.vessel2, 3, 9, 11],
  ]);
  // Cluster 3 — north again, past the middle, mostly swallowed.
  adWall(-100, -1, [
    [P.vessel, -6, 16, 11],
    [P.portrait2, 7, 14.4, 6],
    [P.neon2, -8, 8.4, 3.4],
  ]);

  // Cluster 0 — the near-north wall, right at the billboard lens. The
  // reference's own right-hand columns carry 2.3% of its emissive at cell
  // means up to Y110; the first pass left ours at Y14–30, a dead black band
  // down a fifth of the plate. A canyon is lit on BOTH sides of the lens.
  adWall(86, -1, [
    [P.travel2, -5, 12.2, 7],
    [P.figures2, 5, 11.4, 5.4],
  ]);
  // The near brand panels. §e allows ≤1% of the frame above Y200 and the
  // reference spends 0.58% of it — but a 0.0125 exponential fog puts a
  // 225-ceiling texel at Y196 by 30 m, so EVERY pixel over 200 in this
  // district has to come from a fixture inside ~25 m of the lens. These two
  // are the whole budget, one per night framing, and they are near-white
  // brand panels because that is also where the census's low-sat white
  // lives (13.7% of the reference's emissive pixels).
  mount(t, scene, P.brand, {
    x: 88,
    y: 6.4,
    side: -1,
    w: 5.8,
    h: 7.1,
    spread: 1.9,
    spillAlpha: 0.5,
    hazeAlpha: 0.16,
    reflect: 20,
    reflectAlpha: 0.68,
  });
  mount(t, scene, P.brand2, {
    x: 2,
    y: 6.2,
    side: 1,
    w: 6.8,
    h: 8.4,
    spread: 1.9,
    spillAlpha: 0.5,
    hazeAlpha: 0.16,
    reflect: 18,
    reflectAlpha: 0.68,
  });

  // Cluster 4 — the street vista's near-north wall. Both night framings
  // look west, and the billboard lens starts 86 m east of this one, so a
  // district clustered only for the billboard leaves the street vista
  // looking down an empty run: it measured Y≥60 coverage of 4.7% against
  // the bar's ~9%, with a YHIGH of 47 under the 50–64 window. Every framing
  // needs its own loud wall.
  adWall(-14, -1, [
    [P.vessel2, -7, 15.6, 12],
    [P.portrait, 6, 14.2, 6.4],
    [P.travel, -8, 7.6, 6.6],
    [P.neon3, 6.5, 7.4, 3],
  ]);

  // Singles, spaced far enough apart that the dark between the clusters
  // stays dark. Portrait-dominant, per §e.
  mount(t, scene, P.portrait2, {
    x: 88,
    y: 7.4,
    side: 1,
    w: 4.4,
    h: 6.4,
    spread: 1.9,
    spillAlpha: 0.5,
    reflect: 16,
  });
  mount(t, scene, P.neon, {
    x: 16,
    y: 6.6,
    side: 1,
    w: 6.6,
    h: 3.3,
    spread: 2.0,
    spillAlpha: 0.62,
    reflect: 14,
    reflectAlpha: 0.68,
  });
  mount(t, scene, P.figures, {
    x: -74,
    y: 9.4,
    side: -1,
    w: 3.4,
    h: 6.8,
    spread: 1.8,
    spillAlpha: 0.42,
    reflect: 12,
  });
  mount(t, scene, P.travel, {
    x: -128,
    y: 11,
    side: 1,
    w: 5,
    h: 6.4,
    spread: 1.8,
    spillAlpha: 0.4,
    reflect: 0,
  });
  // Two blade signs standing proud of the wall, addressing the traffic —
  // one per accent family, both inside 30 m of the billboard lens.
  mount(t, scene, P.neon3, {
    x: 84,
    y: 9.6,
    side: 0,
    z: D - 2.4,
    yaw: Math.PI / 2,
    w: 3.2,
    h: 9.6,
    spread: 1.9,
    spillAlpha: 0.34,
    reflect: 16,
    reflectAlpha: 0.6,
  });
  mount(t, scene, P.neon2, {
    x: 74,
    y: 9,
    side: 0,
    z: -D + 2.2,
    yaw: Math.PI / 2,
    w: 2.6,
    h: 7.8,
    spread: 1.9,
    spillAlpha: 0.34,
    reflect: 14,
    reflectAlpha: 0.55,
  });

  // --- Interior-lit frontages ----------------------------------------------
  // §e's ONLY sanctioned window feature: interior-lit boxes with a vertical
  // brightness gradient and dark occluder silhouettes inside. Short runs,
  // low on the wall, at the two framings' near-to-mid distances — never a
  // grid, and never above the first floor.
  const frontage = (x: number, len: number, side: -1 | 1, panel: Panel): void => {
    const map = panel.face.clone();
    map.wrapS = THREE.RepeatWrapping;
    map.repeat.x = Math.max(1, Math.round(len / 22));
    map.needsUpdate = true;
    t.tex(map);
    const quad = new THREE.Mesh(
      t.geo(new THREE.PlaneGeometry(len, 3.4)),
      t.mat(new THREE.MeshBasicMaterial({ map })),
    );
    quad.position.set(x, 2.5, side * (D - 0.35));
    quad.rotation.y = side < 0 ? 0 : Math.PI;
    scene.add(quad);
    // Its own spill onto the pavement and up the wall — the reason a lit
    // frontage reads as premises rather than as a decal.
    const spillMap = panel.spill.clone();
    spillMap.wrapS = THREE.RepeatWrapping;
    spillMap.repeat.x = Math.max(1, Math.round(len / 30));
    spillMap.needsUpdate = true;
    t.tex(spillMap);
    const spill = new THREE.Mesh(
      t.geo(new THREE.PlaneGeometry(len, 9)),
      t.mat(
        new THREE.MeshBasicMaterial({
          map: spillMap,
          transparent: true,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          opacity: 0.42,
        }),
      ),
    );
    spill.position.set(x, 3.4, side * (D - 0.45));
    spill.rotation.y = side < 0 ? 0 : Math.PI;
    scene.add(spill);
    const pool = new THREE.Mesh(
      t.geo(new THREE.PlaneGeometry(len, 13)),
      t.mat(
        new THREE.MeshBasicMaterial({
          map: panel.reflect,
          transparent: true,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          opacity: 0.46,
        }),
      ),
    );
    pool.rotation.set(-Math.PI / 2, 0, side > 0 ? Math.PI : 0);
    pool.position.set(x, 0.04, side * (D - 7));
    scene.add(pool);
  };
  frontage(30, 40, -1, P.interior);
  frontage(-14, 30, 1, P.interior2);
  frontage(-84, 34, -1, P.interior2);
  frontage(72, 26, 1, P.interior);

  // Real recessed windows: an actual reveal, a graded back wall and a body
  // in the opening. Six of them, near-field only — the audit's "5 glazed
  // panels with graded interior light and dark occluder silhouettes".
  const reveal = t.mat(new THREE.MeshBasicMaterial({ color: 0x0d1216 }));
  const occluder = t.mat(new THREE.MeshBasicMaterial({ color: 0x090d10 }));
  for (const [rx, side, warm] of [
    [58, -1, false],
    [50, -1, true],
    [22, 1, true],
    [6, -1, false],
    [-40, -1, true],
    [-60, 1, false],
  ] as const) {
    const wall = side * (D - 0.2);
    const box = new THREE.Mesh(t.geo(new THREE.BoxGeometry(2.6, 2, 1.5)), reveal);
    box.position.set(rx, 4.4, wall - side * 0.75);
    scene.add(box);
    const back = new THREE.Mesh(
      t.geo(new THREE.PlaneGeometry(2.3, 1.75)),
      t.mat(
        new THREE.MeshBasicMaterial({
          map: (() => {
            const canvas = document.createElement("canvas");
            canvas.width = 64;
            canvas.height = 48;
            const c = canvas.getContext("2d");
            if (c) {
              const g = c.createLinearGradient(0, 0, 0, 48);
              g.addColorStop(0, warm ? "#f2d49c" : "#a9d0dc");
              g.addColorStop(0.5, warm ? "#a97b48" : "#4f8898");
              g.addColorStop(1, warm ? "#3a281c" : "#1c3742");
              c.fillStyle = g;
              c.fillRect(0, 0, 64, 48);
            }
            const tex = new THREE.CanvasTexture(canvas);
            tex.colorSpace = THREE.SRGBColorSpace;
            return t.tex(tex);
          })(),
        }),
      ),
    );
    back.position.set(rx, 4.4, wall - side * 1.44);
    back.rotation.y = side < 0 ? 0 : Math.PI;
    scene.add(back);
    const body = new THREE.Mesh(t.geo(new THREE.BoxGeometry(0.45, 1.2, 0.3)), occluder);
    body.position.set(rx + (hash1(SEED, rx) - 0.5) * 1.4, 4.1, wall - side * 1.1);
    scene.add(body);
  }

  // --- Lanterns -------------------------------------------------------------
  // The near-field highlight run: at 0.0125 exponential fog nothing beyond
  // ~25 m can reach Y200, so the ≤1%-at-Y200 budget has to be bought by
  // fixtures INSIDE 25 m of any lens, whatever the lens is pointed at. A
  // line of hanging box lanterns down the canyon centre guarantees two or
  // three of them in every framing's near field.
  //
  // Instanced: 24 lanterns as 60 separate meshes was the district's single
  // largest draw-call line item, and an InstancedMesh renders them in one.
  const lanternFace = (warm: boolean): THREE.CanvasTexture => {
    const canvas = document.createElement("canvas");
    canvas.width = 32;
    canvas.height = 48;
    const c = canvas.getContext("2d");
    if (c) {
      c.fillStyle = "#0b1116";
      c.fillRect(0, 0, 32, 48);
      const g = c.createLinearGradient(0, 3, 0, 45);
      g.addColorStop(0, "#dbe9ec");
      g.addColorStop(0.4, warm ? "#f0a8a8" : "#9fe3ee");
      g.addColorStop(1, warm ? "#7a3040" : "#1c6a78");
      c.fillStyle = g;
      c.fillRect(3, 3, 26, 42);
      c.fillStyle = "#0b1116";
      c.fillRect(3, 23, 26, 2);
      c.fillRect(9, 30, 3, 10);
      c.fillRect(17, 28, 3, 12);
    }
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    return t.tex(tex);
  };
  const LANTERNS = 22;
  const dummy = new THREE.Object3D();
  for (const warm of [false, true] as const) {
    const idx: number[] = [];
    for (let i = 0; i < LANTERNS; i++) if ((i % 3 === 1) === warm) idx.push(i);
    const boxes = new THREE.InstancedMesh(
      t.geo(new THREE.BoxGeometry(0.62, 0.9, 0.62)),
      t.mat(new THREE.MeshBasicMaterial({ map: lanternFace(warm), side: THREE.DoubleSide })),
      idx.length,
    );
    idx.forEach((i, k) => {
      dummy.position.set(
        78 - i * 10.5,
        5.3 + rand2(SEED + 130, i, 0) * 0.5,
        i % 2 === 0 ? -11.5 : 9.5,
      );
      dummy.rotation.set(0, rand2(SEED + 131, i, 0) * 0.9, 0);
      dummy.updateMatrix();
      boxes.setMatrixAt(k, dummy.matrix);
    });
    boxes.instanceMatrix.needsUpdate = true;
    boxes.frustumCulled = false;
    scene.add(boxes);
  }
  underAsset(
    t,
    scene,
    "city-lantern",
    Array.from({ length: LANTERNS }, (_, i) => ({
      x: 78 - i * 10.5,
      y: 5.3 + rand2(SEED + 130, i, 0) * 0.5 - 0.45,
      z: i % 2 === 0 ? -11.5 : 9.5,
      yaw: rand2(SEED + 131, i, 0) * 0.9,
    })),
    true,
  );
  const drops = new THREE.InstancedMesh(
    t.geo(new THREE.CylinderGeometry(0.03, 0.03, 9, 4)),
    t.mat(new THREE.MeshBasicMaterial({ color: 0x151b20 })),
    LANTERNS,
  );
  for (let i = 0; i < LANTERNS; i++) {
    dummy.position.set(78 - i * 10.5, 10.2, i % 2 === 0 ? -11.5 : 9.5);
    dummy.rotation.set(0, 0, 0);
    dummy.updateMatrix();
    drops.setMatrixAt(i, dummy.matrix);
  }
  drops.instanceMatrix.needsUpdate = true;
  drops.frustumCulled = false;
  scene.add(drops);
  // Only every third lantern earns a halo sprite; twenty-two of them was
  // twenty-two draw calls for a glow the fog was already selling.
  for (let i = 0; i < LANTERNS; i += 3) {
    const warm = i % 3 === 1;
    const halo = new THREE.Sprite(
      t.mat(
        new THREE.SpriteMaterial({
          map: hazePatch(t, warm ? "rgba(226,90,110,0.8)" : "rgba(90,200,220,0.8)", i),
          transparent: true,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          opacity: 0.2,
        }),
      ),
    );
    halo.position.set(78 - i * 10.5, 5.3, i % 2 === 0 ? -11.5 : 9.5);
    halo.scale.set(6.5, 5.2, 1);
    scene.add(halo);
  }

  // --- Kiosks ---------------------------------------------------------------
  // The near-field anchor and the district's warmest human note: a counter,
  // a canopy, a lit serving hatch, stools. Each night framing has one at
  // 18–25 m, inside the fog's highlight window.
  for (const [kx, kz, kyaw, panel] of [
    [72, -13, 1.15, P.hatchA],
    [-8, -6, 2.05, P.hatchB],
  ] as const) {
    const kiosk = new THREE.Group();
    underAsset(t, kiosk, "city-kiosk", [
      { x: 0, y: 0, z: 0.2, width: 5.6, height: 3.3, depth: 3.6 },
    ]);
    const hatch = new THREE.Mesh(
      t.geo(new THREE.PlaneGeometry(4.1, 2.1)),
      t.mat(new THREE.MeshBasicMaterial({ map: panel.face })),
    );
    hatch.position.set(0, 2.0, 1.21);
    kiosk.add(hatch);
    underAsset(
      t,
      kiosk,
      "bar-stool",
      [-1.6, -0.2, 1.2].map((x) => ({ x, y: 0, z: 2.3, width: 0.4, height: 0.8, depth: 0.4 })),
    );
    const spill = new THREE.Mesh(
      t.geo(new THREE.PlaneGeometry(10, 9)),
      t.mat(
        new THREE.MeshBasicMaterial({
          map: panel.spill,
          transparent: true,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          opacity: 0.4,
        }),
      ),
    );
    spill.rotation.x = -Math.PI / 2;
    spill.position.set(0, 0.05, 3);
    kiosk.add(spill);
    kiosk.position.set(kx, 0, kz);
    kiosk.rotation.y = kyaw;
    scene.add(kiosk);
  }

  const holo = buildHologram(t, scene);

  // --- Near-field grounding -------------------------------------------------
  // Box frames, cable bundles, vent greeble and crates in both framings'
  // lower thirds: the clutter list §e names, and the thing that gives a
  // fogged corridor its depth cues.

  const cableMat = t.mat(new THREE.MeshStandardMaterial({ color: 0x141a1f, roughness: 1 }));
  for (const [gx, gz, seed] of [
    [66, -21, 1],
    [40, 23, 2],
    [4, -23, 3],
    [-26, 21, 4],
    [-62, -22, 5],
    [-104, 20, 6],
    [88, 19, 7],
    [-8, 24, 8],
    [84, -9, 9],
    [66, 6, 10],
    [50, -15, 11],
    [-2, -12, 12],
    [-20, 4, 13],
    [-36, -14, 14],
  ] as const) {
    const cluster = new THREE.Group();
    const n = 2 + Math.floor(rand2(SEED + 90, seed, 0) * 3);
    for (let i = 0; i < n; i++) {
      const s = 0.7 + rand2(SEED + 91, seed, i) * 0.9;
      underAsset(t, cluster, "frontier-crate", [
        {
          x: (rand2(SEED + 92, seed, i) - 0.5) * 3.4,
          y: 0,
          z: (rand2(SEED + 93, seed, i) - 0.5) * 2.2,
          width: s * 1.3,
          height: s,
          depth: s * 1.1,
          yaw: rand2(SEED + 94, seed, i) * 1.6,
        },
      ]);
    }
    underAsset(t, cluster, "city-roof-plant", [
      { x: 2.4, y: 0, z: -0.6, width: 1.3, height: 2.4, depth: 1.1 },
    ]);
    const duct = new THREE.Mesh(t.geo(new THREE.CylinderGeometry(0.24, 0.24, 4.2, 7)), cableMat);
    duct.rotation.z = Math.PI / 2;
    duct.position.set(0.6, 2.9, -0.6);
    cluster.add(duct);
    cluster.position.set(gx, 0, gz);
    cluster.rotation.y = rand2(SEED + 95, seed, 0) * Math.PI;
    scene.add(cluster);
  }
  /**
   * THE CROSS-CANYON CABLE RUNS — WAVE 14, and the previous shape of them
   * is worth naming. A Catmull-Rom through three hand-placed points is a
   * spline that PASSES THROUGH a guessed low point: it leaves the anchor
   * horizontally, dives, and comes back up, which is what a thrown rope
   * looks like in the instant before it settles and nothing like a hung
   * cable. A catenary leaves its anchor at the tangent the tension sets
   * and is steepest AT the anchor — the half a viewer actually reads,
   * because it is the half touching a building.
   *
   * There are twice as many now, at five heights, crossing rather than
   * running parallel. The canyon's whole silhouette above head height was
   * three straight ceiling ribs and five near-identical sags.
   */
  for (const [cx, sag, cy, skew] of [
    [72, 1.6, 17, 0.9],
    [52, 2.3, 12.5, -1.4],
    [30, 2.1, 14.5, 1.2],
    [8, 1.4, 18.5, -0.6],
    [-14, 1.8, 16, 1.6],
    [-34, 2.6, 11.5, -1.1],
    [-56, 2.4, 13.5, 0.7],
    [-78, 1.5, 17.5, -1.7],
    [-100, 1.7, 15.5, 1.3],
  ] as const) {
    for (let k = 0; k < 3; k++) {
      scene.add(
        new THREE.Mesh(
          t.geo(
            catenaryGeometry(
              { x: cx + k * 0.9, y: cy, z: -D + 1 },
              { x: cx + k * 0.9 + skew, y: cy - 0.4, z: D - 1 },
              sag + k * 0.18,
              0.06,
              12,
            ),
          ),
          cableMat,
        ),
      );
    }
  }

  // Structural supports keep the shared collision footprints.
  underAsset(
    t,
    scene,
    "city-pillar",
    UNDERSTREET_PILLARS.map((box) => ({
      x: (box.minX + box.maxX) / 2,
      y: 0,
      z: (box.minZ + box.maxZ) / 2,
      width: box.maxX - box.minX,
      height: DECK_Y,
      depth: box.maxZ - box.minZ,
    })),
  );
  // Compression arches occupy the upper canyon, meeting the existing deck.
  underAsset(
    t,
    scene,
    "city-support-arch",
    [-108, -60, -12, 36, 84].map((x) => ({
      x,
      y: 0,
      z: 0,
      yaw: Math.PI / 2,
      scale: [(D * 2) / 38, 1, 1],
    })),
  );
  underAsset(
    t,
    scene,
    "city-pallet",
    UNDERSTREET_CLUTTER.map((box) => ({
      x: (box.minX + box.maxX) / 2,
      y: 0,
      z: (box.minZ + box.maxZ) / 2,
      width: box.maxX - box.minX,
      height: 2.4,
      depth: box.maxZ - box.minZ,
    })),
  );

  // --- The Undertow's door and the gate up to the crown ---------------------
  const door = new THREE.Mesh(
    t.geo(new THREE.BoxGeometry(3.6, 3, 0.4)),
    t.mat(new THREE.MeshBasicMaterial({ color: 0x05080a })),
  );
  door.position.set(-12, 1.5, -D + 0.2);
  scene.add(door);
  const doorTop = new THREE.Mesh(
    t.geo(new THREE.BoxGeometry(4.1, 0.2, 0.5)),
    t.mat(
      new THREE.MeshStandardMaterial({
        color: 0xff3b6b,
        emissive: 0xff3b6b,
        emissiveIntensity: 1.1,
      }),
    ),
  );
  doorTop.position.set(-12, 3.1, -D + 0.25);
  scene.add(doorTop);

  underAsset(t, scene, "city-gate", [
    { x: W - 4, y: 0, z: 0, yaw: Math.PI / 2, width: 10.6, height: 7.6, depth: 1.5 },
  ]);
  const gateRamp = new THREE.Mesh(
    t.geo(new THREE.BoxGeometry(30, 0.4, 9)),
    t.mat(new THREE.MeshStandardMaterial({ color: 0x1b2126, roughness: 0.9 })),
  );
  gateRamp.position.set(W + 10, 1.4, 0);
  gateRamp.rotation.z = 0.12;
  scene.add(gateRamp);

  // Gaslamps: near-white mantles on posts. Their point lights are what put
  // a hard specular on the wet paving, so they earn their cost twice.
  // Y190, not Y217: a gas mantle stacked with its own pool of lit paving
  // was the last thing in the district reaching 255.
  const lampMat = t.mat(new THREE.MeshBasicMaterial({ color: 0xbdc3c1 }));

  for (const [lx, lz] of [
    [-70, 10],
    [4, -8],
    [78, 9],
  ] as const) {
    underAsset(t, scene, "city-lamp", [
      { x: lx, y: 0, z: lz, width: 0.6, height: 4.7, depth: 0.6 },
    ]);
    const core = new THREE.Mesh(t.geo(new THREE.SphereGeometry(0.16, 8, 6)), lampMat);
    core.position.set(lx, 4.5, lz);
    scene.add(core);
    const spill = new THREE.PointLight(0xc4cdd0, 8.5, 13, 1.9);
    spill.position.set(lx, 4.2, lz);
    scene.add(spill);
  }

  const skyTraffic = new SkyTraffic(t);
  scene.add(skyTraffic.points);

  // Body light. Enough cool fill to read an avatar and to hold the paving at
  // the fog floor — never enough to lift the massing out of silhouette (the
  // massing is unlit MeshBasic, so this touches only floor, props and
  // bodies). Point lights sit at the loud clusters, so a player walking past
  // an ad wall is lit by it.
  // Solved against the §e envelope, not chosen. At 7.4 the paving crossed
  // Y60 over most of the lower third and the frame's Y≥60 coverage hit
  // 16.7% against a ≤10% bar, with YHIGH at 72.9 against a 50–64 window —
  // i.e. the district got bright in its BULK, which is the opposite of the
  // reference's compressed midrange. 5.8 holds the near paving at Y28 and
  // the mid at Y33, inside the fog floor and under the threshold.
  scene.add(new THREE.HemisphereLight(0x62798a, 0x333e48, 6.9));
  // AND A PROBE, which this district has never had. There is no sun down
  // here, so the disc is a moon and it is nearly nothing; what a brass
  // fitting, a window or a wet paving slab in the Understreet actually
  // reflects is the ad walls and the window rows, and those come from BELOW
  // and behind. That is what `glow` is. It is the term that stops every metal
  // in the two night vistas being a flat dark shape with a single point
  // highlight on it.
  installProbe(scene, t, {
    horizon: HAZE,
    zenith: 0x141c26,
    ground: 0x2a2030,
    sunDir: new THREE.Vector3(0.3, 0.72, -0.62).normalize(),
    sunColor: 0xbcc6e0,
    sunIrradiance: 0.9,
    halo: [[0.18, 6]],
    glow: [0.55, 1.3],
    glowColor: 0x7a4f86,
    // Low, and for the same reason as the city's: the §e envelope for this
    // district is measured on how much of the floor crosses Y60, and a probe's
    // irradiance lands on the floor. What the metals need is the glow's
    // RADIANCE, which the response set's per-class gain multiplies on its own.
    intensity: 0.28,
  });
  if (scene.environment) bindAuthoredEnvironment(scene, scene.environment, 0.28);
  // Broad anchored pools of lit paving under each cluster. §e's haze bar is
  // that the glow is "light-source-anchored, not uniform", and the same rule
  // holds for the ground: a street under an ad wall is bright, a street
  // between them is not, and a hemisphere light strong enough to lift the
  // whole floor would flatten exactly the gradient that sells the depth.
  for (const [px, pz, tint, rw, rd, alpha] of [
    [46, -18, "rgba(70,200,224,0.85)", 62, 40, 0.13],
    [-48, 17, "rgba(232,72,96,0.85)", 56, 38, 0.11],
    [86, -16, "rgba(80,190,214,0.85)", 40, 32, 0.1],
    [62, 6, "rgba(70,200,224,0.85)", 44, 40, 0.09],
    [-30, 5, "rgba(232,72,96,0.85)", 40, 36, 0.08],
  ] as const) {
    const pool = new THREE.Mesh(
      t.geo(new THREE.PlaneGeometry(rw, rd)),
      t.mat(
        new THREE.MeshBasicMaterial({
          map: hazePatch(t, tint, px | 0),
          transparent: true,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          opacity: alpha,
        }),
      ),
    );
    pool.rotation.x = -Math.PI / 2;
    pool.position.set(px, 0.02, pz);
    scene.add(pool);
  }

  const clusterLights: ReadonlyArray<readonly [number, number, number, number, number]> = [
    [40, 12, -D + 6, 0x3ec6d8, 26],
    [-52, 12, D - 6, 0xe8546a, 22],
    [66, 11, 8, 0x3ec6d8, 20],
    [-28, 10, 6, 0xe8546a, 16],
    [W - 6, 5, 0, 0xd8b04a, 12],
  ];
  for (const [lx, ly, lz, color, power] of clusterLights) {
    const light = new THREE.PointLight(color, power, 34, 1.8);
    light.position.set(lx, ly, lz);
    scene.add(light);
  }

  // --- Poster boxes fed by the real bill ------------------------------------
  // The Nights, advertised in the dark. The bill drives the art through
  // SHAPE, never spelling: the title's length and first character seed a
  // different forged poster per Night, and none of them is words.
  class BillBox {
    private group: THREE.Group | undefined;
    constructor(
      private slot: number,
      private opts: MountOptions,
    ) {}
    paint(nights: readonly NightBill[]): void {
      const night = nights[this.slot % Math.max(1, nights.length)];
      const key = night
        ? night.title.length * 7 + night.title.charCodeAt(0) + this.slot
        : this.slot;
      const kind = key % 3;
      const seed = 300 + key;
      const next = makePanel(
        t,
        208,
        288,
        (c, cw, ch) =>
          kind === 0
            ? drawPortraitPoster(c, cw, ch, seed)
            : kind === 1
              ? drawTravelPoster(c, cw, ch, seed)
              : drawFigureStrip(c, cw, ch, seed),
        { seed, tone: 0.13, grain: 0.055, gain: 1.2, spillBoost: 1.3, reflectGain: 1.3 },
      );
      if (this.group) scene.remove(this.group);
      this.group = mount(t, scene, next, this.opts);
    }
  }
  const bills = [
    new BillBox(0, {
      x: -4,
      y: 5.2,
      side: -1,
      w: 4.2,
      h: 5.8,
      spread: 1.9,
      spillAlpha: 0.5,
      reflect: 15,
    }),
    new BillBox(1, {
      x: 26,
      y: 5,
      side: 1,
      w: 4,
      h: 5.5,
      spread: 1.9,
      spillAlpha: 0.5,
      reflect: 15,
    }),
    new BillBox(2, {
      x: -60,
      y: 5.4,
      side: -1,
      w: 4.2,
      h: 5.8,
      spread: 1.9,
      spillAlpha: 0.45,
      reflect: 14,
    }),
  ];
  for (const b of bills) b.paint([]);

  let elapsed = 0;

  return {
    scene,
    worldAnchor,
    cameraFar: 900,
    // The night district was graded under LINEAR output and passed its
    // measured anchors that way; the authored operator ships with the scene.
    toneMapping: THREE.NoToneMapping,
    // …and so does its post treatment. The threshold sits UNDER paper
    // white, which would be ruinous in daylight and is safe here: this
    // set has a three-stop gap between its paving and its sign faces with
    // nothing in it. That is what finally gets the fixtures bleeding onto
    // the geometry around them — §d's "glow bleeding onto nearby
    // geometry" — without moving the measured emissive census (Y≥60
    // coverage 9.67% → 9.76% against the reference's 9.28%).
    post: NIGHT_DISTRICT_POST,
    origin: () => ({ x: 0, z: 0 }),
    groundY: () => 0,
    tick(dt: number) {
      elapsed += dt;
      const time = holo.uniforms.time;
      if (time) time.value = elapsed;
      skyTraffic.tick(dt);
    },
    setLive() {
      // The booths are indoors; the street only advertises them.
    },
    setNights(nights) {
      for (const b of bills) b.paint(nights);
    },
    dispose() {
      holo.dispose();
      t.dispose();
    },
  };
}
